import { ResponseHeaders } from '@/models/common/headers.model';
import { DEFAULT_LIMIT } from '@/constants/common/constants';
import { api } from '@/services/http.service';
import { showErrorNotification } from '@/utils/show-notification.util';
import { defineStore } from 'pinia';
import { TopWinListItem } from '@/models/top-list/top-win-list.model';
import { TopPlayerListItem } from '@/models/top-list/top-player-list.model';
import { TopListRequestDateParameters } from '@/models/top-list/top-list-request-date-parameters.model';
import { TopListPayload } from '@/models/top-list/top-list-payload.model';
import { TopWinListResponse } from '@/models/top-list/top-win-list-response.model';
import { TopPlayerListResponse } from '@/models/top-list/top-player-list-response.model';
import { TopListTableNames } from '@/models/top-list/top-list-table-names.model';
import { TOP_LIST_TABLES_MAP } from '@/constants/top-list/top-list-tables.constant';
import { TopListItem } from '@/models/top-list/top-list-item.model';
import { cloneDeep } from 'lodash';
import { formatUnixToString } from '@marfa/bo-utils';

interface TopListState {
  isTopListLoading: boolean;
  topList: TopListItem[];
  activeTableName: TopListTableNames;
  prevPageToken: string;
  nextPageToken: string;
  limit: number;
  sort: string;
}

export const useTopListStore = defineStore('TopListStore', {
  state: (): TopListState => ({
    isTopListLoading: false,
    topList: [],
    activeTableName: TopListTableNames.TOP_WINS,
    prevPageToken: '',
    nextPageToken: '',
    limit: DEFAULT_LIMIT,
    sort: '',
  }),
  actions: {
    setTopListLoading(isLoading: boolean): void {
      this.isTopListLoading = isLoading;
    },
    setPrevPageToken(prevPageToken: string): void {
      this.prevPageToken = prevPageToken;
    },
    setNextPageToken(nextPageToken: string): void {
      this.nextPageToken = nextPageToken;
    },
    resetPaginationTokens(): void {
      this.setNextPageToken('');
      this.setPrevPageToken('');
    },
    processResponseHeaders(headers: Record<string, string>): void {
      if (headers[ResponseHeaders.PREV_PAGE_TOKEN_HEADER] || headers[ResponseHeaders.NEXT_PAGE_TOKEN_HEADER]) {
        this.setPrevPageToken(headers[ResponseHeaders.PREV_PAGE_TOKEN_HEADER] ?? '');
        this.setNextPageToken(headers[ResponseHeaders.NEXT_PAGE_TOKEN_HEADER] ?? '');

        return;
      }

      this.resetPaginationTokens();
    },
    setSort(sortOrder: string): void {
      this.sort = sortOrder;
    },
    getTopWinList(
      whitelabelSlug: string,
      requestTopListDatesParams: TopListRequestDateParameters,
    ): Promise<TopWinListItem[]> {
      this.setTopListLoading(true);

      const requestPayload: TopListPayload = {
        limit: this.limit,
        nextPageToken: this.nextPageToken,
        ...requestTopListDatesParams,
      };

      return api.topList.getTopWinListRequest.perform(whitelabelSlug, requestPayload)
        .then(({ data, headers }: TopWinListResponse) => {
          const rawData = data.map((dataItem: TopWinListItem): TopListItem => {
            return {
              rowKey: crypto.randomUUID(),
              playerId: dataItem.playerId,
              tagList: dataItem.tagList,
              gameName: dataItem.gameName,
              stakeAmount: `€ ${dataItem.stakeAmount}`,
              currency: dataItem.currency,
              winAmount: `€ ${dataItem.winAmount}`,
              endActionTime: formatUnixToString(dataItem.endActionTime, true),
              roundId: dataItem.roundId,
              playerEmail: dataItem.playerEmail,
              maskedFieldList: dataItem.maskedFieldList,
            };
          });

          this.topList.push(...cloneDeep(rawData));
          this.processResponseHeaders(headers);

          return data;
        })
        .catch((error) => {
          showErrorNotification(error);

          return [];
        })
        .finally(() => {
          this.setTopListLoading(false);
        });
    },
    getTopPlayerList(
      whitelabelSlug: string,
      requestTopListDatesParams: TopListRequestDateParameters,
    ): Promise<TopPlayerListItem[]> {
      this.setTopListLoading(true);

      const requestPayload: TopListPayload = {
        limit: this.limit,
        sort: this.sort,
        nextPageToken: this.nextPageToken,
        ...requestTopListDatesParams,
      };

      return api.topList.getTopPlayerListRequest.perform(whitelabelSlug, requestPayload)
        .then(({ data, headers }: TopPlayerListResponse) => {
          const rawData = data.map((dataItem: TopPlayerListItem): TopListItem => {
            return {
              rowKey: crypto.randomUUID(),
              playerId: dataItem.playerId,
              playerEmail: dataItem.playerEmail,
              tagList: dataItem.tagList,
              stakeAmount: `€ ${dataItem.stakeAmount}`,
              winAmount: `€ ${dataItem.winAmount}`,
              currency: dataItem.currency,
              ggrAmount: `€ ${dataItem.ggrAmount}`,
              ngrAmount: `€ ${dataItem.ngrAmount}`,
              rtpPercent: `${dataItem.rtpPercent} %`,
              maskedFieldList: dataItem.maskedFieldList,
            };
          });

          this.topList.push(...cloneDeep(rawData));
          this.processResponseHeaders(headers);

          return data;
        })
        .catch((error) => {
          showErrorNotification(error);

          return [];
        })
        .finally(() => {
          this.setTopListLoading(false);
        });
    },
    getActiveTableList(whitelabelSlug: string, dateTimeRange: TopListRequestDateParameters): void {
      if (this.activeTableName === TopListTableNames.TOP_WINS) {
        this.getTopWinList(whitelabelSlug, dateTimeRange);

        return;
      }
      this.setSort(TOP_LIST_TABLES_MAP[this.activeTableName].sort);
      this.getTopPlayerList(whitelabelSlug, dateTimeRange);
    },
    clearTopList(): void {
      this.topList = [];
    },
  },
});
